import { useEffect, useRef } from "react";

/**
 * Keyboard shortcuts: "mod+k" (⌘ on Mac, Ctrl elsewhere), single keys ("j", "?",
 * "shift+a"), and two-key sequences ("g i"). Ignored while typing in a field unless
 * `inInputs` is set, and while a modal dialog is open unless `inDialogs` is set.
 */
export type HotkeyMap = Record<string, (e: KeyboardEvent) => void>;

export function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
export const modKey = isMac ? "⌘" : "Ctrl";

function normalize(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push("mod");
  if (e.altKey) parts.push("alt");
  const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
  // shift is implied by characters like "?" and uppercase letters
  if (e.shiftKey && key.length === 1 && /[a-z]/.test(key)) parts.push("shift");
  parts.push(key === " " ? "space" : key);
  return parts.join("+");
}

export function useHotkeys(map: HotkeyMap, opts: { enabled?: boolean; inInputs?: boolean; inDialogs?: boolean } = {}) {
  const ref = useRef(map);
  ref.current = map;
  const { enabled = true, inInputs = false, inDialogs = false } = opts;
  useEffect(() => {
    if (!enabled) return;
    let pending: string | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      const combo = normalize(e);
      const typing = isTypingTarget(e.target);
      if (typing && !inInputs && !combo.startsWith("mod+") && combo !== "Escape") return;
      if (!inDialogs && document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]') && !combo.startsWith("mod+")) return;
      const keys = Object.keys(ref.current);
      if (pending) {
        const seq = `${pending} ${combo}`;
        pending = null;
        clearTimeout(timer);
        if (ref.current[seq]) {
          e.preventDefault();
          ref.current[seq]!(e);
          return;
        }
      }
      if (keys.some((k) => k.startsWith(`${combo} `))) {
        pending = combo;
        timer = setTimeout(() => (pending = null), 900);
        return;
      }
      const fn = ref.current[combo];
      if (fn) {
        e.preventDefault();
        fn(e);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(timer);
    };
  }, [enabled, inInputs, inDialogs]);
}
